import {
  Component,
  ElementRef,
  inject,
  input,
  model,
  output,
  PLATFORM_ID,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, Subject } from 'rxjs';
import { ComponentSharedModule } from '@app/modules/component.shared.module';
import {
  MobiUiDatepickerComponents,
  MobiUiDropdownComponents,
  MobiUiInputComponents,
} from '@mobi/rwc-ui-components-ng-jslib';
import { MultiSelectModule } from 'primeng/multiselect';
import { FormsModule } from '@angular/forms';
import { BreakpointService } from '@app/services/breakpoint.service';
import { SelectComponent } from '@app/components/view/select/select.component';
import { TealiumUtagService } from '@app/services/utag.service';
import { TagsFilterComponent } from '@app/components/view/tags-filter/tags-filter.component';

@Component({
  selector: 'mobi-view-filters',
  standalone: true,
  imports: [
    ComponentSharedModule,
    MobiUiDatepickerComponents,
    MobiUiInputComponents,
    MobiUiDropdownComponents,
    MultiSelectModule,
    FormsModule,
    SelectComponent,
    TagsFilterComponent,
  ],
  templateUrl: './view-filters.component.html',
  styleUrls: ['./view-filters.component.scss'],
})
export class ViewFiltersComponent {
  readonly viewId = input('');
  readonly title = input('');
  readonly scrollable = input(false);
  readonly autofocus = input(false);
  readonly exposedFilters = input<any[]>([]);
  readonly filters = model<{ [key: string]: any }>({});
  readonly centerFilters = input(false);
  readonly groupFilter = input<string[]>([]);
  readonly hideFilters = input<string[]>([]);

  readonly filterUpdate = output<{ key: string; value: any; immediate?: boolean }>();
  readonly selectionUpdate = output<{ key: string; value: any }>();

  readonly filtersRef = viewChild<ElementRef<HTMLDivElement>>('filtersRef');

  #platformId = inject(PLATFORM_ID);
  #tealium = inject(TealiumUtagService);
  breakpoint = inject(BreakpointService);

  readonly #textFilterChange$ = new Subject<{ key: string; value: string }>();

  constructor() {
    this.#textFilterChange$
      .pipe(debounceTime(1000), takeUntilDestroyed())
      .subscribe(({ key, value }) => this.#sendFilterTracking(key, value));
  }

  get yearArray() {
    const currentYear: number = new Date().getFullYear();
    const startYear: number = 2021;
    const yearsArray: any[] = [];

    for (let year = startYear; year <= currentYear; year++) {
      yearsArray.push({ key: year.toString(), value: year.toString() });
    }

    return yearsArray.reverse();
  }

  isGrouped(id: string) {
    return this.groupFilter().includes(id);
  }

  onTextFilterChange(key: string, value: string) {
    if (value.length > 2 || value === '') {
      this.filterUpdate.emit({ key, value });
      this.#textFilterChange$.next({ key, value });
    }
  }

  updateFilters(key: string, value: any, immediate: boolean = false) {
    if (value === null) {
      this.filterUpdate.emit({ key, value: null, immediate: true });
      return;
    }

    this.filterUpdate.emit({ key, value, immediate });

    this.#tealium.link({
      tealium_event: 'interaction_event',
      interaction_type: 'filter',
      element_type: 'filter',
      element_name: key,
      element_value: value,
      element_id: `filger-${key}-${value}`,
      ...this.#tealium.getDataFromDom(this.filtersRef()!.nativeElement),
    });
  }

  #sendFilterTracking(key: string, value: string) {
    this.#tealium.link({
      tealium_event: 'interaction_event',
      interaction_type: 'filter',
      element_type: 'filter',
      element_name: key,
      element_value: value,
      element_id: `filger-${key}-${value}`,
      ...this.#tealium.getDataFromDom(this.filtersRef()!.nativeElement),
    });
  }

  updateSelection(key: string, value: any) {
    this.selectionUpdate.emit({ key, value });
  }

  selectedOption(name: string) {
    return this.filters()[name] || '';
  }
}
